// Phase 15 Plan 05 Task 3 — Net wrappers for the profile surface.
// Phase 19 Plan 03 Task 3 — Wire shape updated for META-04 + DEC-029:
//   - `lexicon_size` is REMOVED from the wire (Lexicon feature dropped per DEC-029).
//   - `per_ladder_elos` is the active 8-keyed projection of {rating, rd, vol} | null
//     per DEC-010 ladder. Top-level value is ALWAYS the 8-key object (never null);
//     individual per-ladder values may be null.
//
// Two endpoints:
//   - GET /api/profile/{userId}     → public profile view (B12-firewalled; T-15-02)
//   - PATCH /api/account/display-name → change own display name (24-char / 90d throttle / profanity)
//
// IMPORTANT: there is NO `POST /api/account/edit-name` endpoint. The
// EditNameModal (Plan 08) MUST call `changeDisplayName()` here, which hits
// the existing Phase 14 `PATCH /api/account/display-name`.

import type { components } from '@runegambit/api-types'
import { apiFetch } from './client'
import { createIdempotencyKey } from './idempotency'

// LOCAL MIRROR — replace with codegen re-export after Plan 19-04 regenerates
// packages/api-types/generated.ts. The shape mirrors the backend's
// apps/backend/src/profile/per-ladder-elos.ts exports verbatim (Plan 19-01).
//
// vol (Glicko-2 sigma) is on the wire for future operator/debug surfaces; the
// basic UI per D-019-T3-A only renders rating + RD ("1500 ±350").
export interface PerLadderEloEntry {
  readonly rating: number // Math.round(mu) — RATING SCALE 1500-centered
  readonly rd: number // Math.round(phi) — rating-scale RD (350 floor)
  readonly vol: number // sigma — Glicko-2 volatility (float, no rounding)
}

// 8 DEC-010 ladder keys — 6 ranked (en/sv × standard_4 / open_3 / purist)
// + 2 casual (en/sv). Always a populated 8-key object on the wire per
// Plan 19-01 D-019-T3; per-ladder values may be null (unrated).
export type PerLadderElos = {
  readonly [K in
    | 'standard_4_en'
    | 'standard_4_sv'
    | 'open_3_en'
    | 'open_3_sv'
    | 'purist_en'
    | 'purist_sv'
    | 'casual_en'
    | 'casual_sv']: PerLadderEloEntry | null
}

// Codegen still emits the OLD shape (`lexicon_size: number | null`,
// `per_ladder_elos: Record<string, number> | null`); Plan 19-04 owns the
// regeneration. Plan 20-08 (parallel sibling) will further regenerate to
// include `achievements` + `inventory` from Plan 20-04's wire-shape drift.
// Until both regenerations land we OMIT lexicon_size and OVERRIDE
// per_ladder_elos to the canonical Plan 19-01 wire shape, AND extend with
// `achievements` + `inventory` string arrays (Plan 20-04 stable-shape
// guarantee: always present, always arrays, empty for new users).
type CodegenProfile = components['schemas']['PublicProfileResponse']
export type PublicProfileResponse = Omit<
  CodegenProfile,
  'lexicon_size' | 'per_ladder_elos'
> & {
  readonly per_ladder_elos: PerLadderElos
  /**
   * Plan 20-04 META-05 — unlocked achievement ids (stable-shape: always
   * an array, empty for new users). Plan 20-08 regenerates the codegen
   * to fold these into the canonical PublicProfileResponse schema.
   */
  readonly achievements: ReadonlyArray<string>
  /**
   * Plan 20-04 META-06 — owned cosmetic ids (stable-shape: always an
   * array, empty for new users). Sources: level_reward / purchase /
   * achievement (Plan 20-02 enum). Plan 20-08 regenerates the codegen.
   */
  readonly inventory: ReadonlyArray<string>
}

/**
 * GET /api/profile/{userId} — public profile envelope.
 *
 * Server projects the response per caller identity (T-15-02):
 *   - if userId === self  → is_self: true, friend_code + account_flags populated
 *   - if userId !== self  → is_self: false, friend_code + account_flags = null
 *
 * Phase 19 (META-04, DEC-029): the wire shape no longer carries `lexicon_size`
 * (Lexicon feature dropped per DEC-029); `per_ladder_elos` is the active
 * 8-keyed projection of `{rating, rd, vol} | null` per DEC-010 ladder.
 */
export async function fetchProfileById(
  userId: string,
): Promise<PublicProfileResponse> {
  return apiFetch<PublicProfileResponse>(`/api/profile/${userId}`, 'GET')
}

/**
 * Response shape for PATCH /api/account/display-name.
 * Server enforces 24-char max + 90-day throttle + profanity filter.
 */
export interface ChangeDisplayNameResponse {
  readonly display_name: string
  readonly changed_at: number
}

/**
 * PATCH /api/account/display-name — change own display name.
 *
 * Used by Plan 08's EditNameModal. The endpoint is REAL (Phase 14 ships
 * the handler); do NOT call any phantom `POST /api/account/edit-name`.
 *
 * Mutating op → carries a fresh `Idempotency-Key` (UUIDv4) per call. On
 * transient 5xx, apiFetch retries once with the SAME key so a successful
 * mutation isn't double-applied.
 *
 * 4xx surfaces ApiError (caller surfaces the i18n string from the body's
 * `errors[0].field`); 401 clears auth.
 */
export async function changeDisplayName(
  displayName: string,
): Promise<ChangeDisplayNameResponse> {
  return apiFetch<ChangeDisplayNameResponse>(
    '/api/account/display-name',
    'PATCH',
    {
      body: { display_name: displayName },
      idempotencyKey: createIdempotencyKey(),
    },
  )
}
